import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { users } from './user'

/** Vendor-supplied, licensed for a fixed term. Purged at `licenseExpiresAt`. */
export interface ListAttributes {
  street?: string | null
  postalCode?: string | null
  city?: string | null
  municipality?: string | null
  region?: string | null
  language?: string | null
  /** The vendor's normalized address key, e.g. '36600*hannantie5'. */
  addressHash?: string | null
}

export const callingListEntries = pgTable(
  'calling_list_entries',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    sourceBatch: text().notNull(),
    licenseExpiresAt: getTimestampWithTimezone().notNull(),
    // Everything the vendor told us. Refreshed by a re-import, emptied by the
    // licence purge — see docs/calling-list-semantics.md §7.
    attributes: typedJsonb<ListAttributes>('attributes'),
    raw: typedJsonb('raw'),
    purgedAt: getTimestampWithTimezone(),
  },
  (table) => [
    ...getBaseIndexes('calling_list_entries', table),
    // One entry per purchase, not per person. A person who turns up on a second
    // bought list gets a second row: each carries its own licence clock, so the
    // first can be destroyed on schedule while the second is still live, and a
    // campaign filtering on the first batch does not lose them when the second
    // arrives. See docs/calling-list-semantics.md §1.
    uniqueIndex('calling_list_entries_user_batch_unique_idx')
      .on(table.userId, table.sourceBatch)
      .where(sql`deleted_at IS NULL`),
    // Every audience facet is a correlated EXISTS on this column.
    index('calling_list_entries_user_id_idx').on(table.userId),
    index('calling_list_entries_source_batch_idx').on(table.sourceBatch),
    index('calling_list_entries_license_expires_at_idx').on(
      table.licenseExpiresAt,
    ),
  ],
)

export const callingListEntriesRelations = relations(
  callingListEntries,
  ({ one }) => ({
    user: one(users, {
      fields: [callingListEntries.userId],
      references: [users.id],
    }),
  }),
)
